import { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { Calendar as CalendarIcon, Check, ChevronDown, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { getEventsForDay } from "@/components/pages/calendar/calendar-data";
import { getAuthUser } from "@/lib/api/token-store";
import { requestOpenCalendarAppointment } from "@/lib/calendar/calendar-navigation";
import { cn } from "@/lib/utils";
import { setUserPreferences } from "@/lib/stores/user-preference-store";

const REFRESH_INTERVAL_MS = 60000;
const NOW_TICK_MS = 30000;

function ownerMatches(ownerUserId) {
  return String(getAuthUser()?.id || "").trim() === String(ownerUserId || "").trim();
}

/**
 * Clinic selector options from the tenant directory: the SHORT name is the label ("Orchard",
 * "Bedok"), sorted A→Z — but several locations can share one short name (Orchard has CAMDEN,
 * L12 and L6), so colliding labels get the distinguishing full name appended
 * ("Orchard · CAMDEN"). Pure — exported for tests.
 */
export function buildClinicOptions(tenants) {
  const options = (Array.isArray(tenants) ? tenants : [])
    .map((tenant) => {
      const id = String(tenant?.id || "").trim();
      const short = String(tenant?.shortName || "").trim();
      const name = String(tenant?.name || "").trim();
      return { id, label: short || name || id, name };
    })
    .filter((option) => option.id);
  const labelCounts = new Map();
  for (const option of options) {
    const key = option.label.toLowerCase();
    labelCounts.set(key, (labelCounts.get(key) || 0) + 1);
  }
  return options
    .map((option) => {
      const collides = (labelCounts.get(option.label.toLowerCase()) || 0) > 1;
      const nameDiffers = option.name && option.name.toLowerCase() !== option.label.toLowerCase();
      return collides && nameDiffers
        ? { ...option, label: `${option.label} · ${option.name}` }
        : option;
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The WHOLE day, oldest first — the card scrolls rather than trimming, so a morning visit is
 * still reachable at 5pm.
 *
 * `nextId` is the first visit that has not started (the highlighted one); `anchorId` is the row
 * the card scrolls itself to so it opens at the current moment — the next visit, or the last one
 * once the day is over. Pure — exported for tests.
 */
export function buildDayTimeline(events, now) {
  const rows = [...events].sort((a, b) => a.startAt.toMillis() - b.startAt.toMillis());
  const nextIndex = rows.findIndex((event) => event.startAt >= now);
  const anchorIndex = nextIndex >= 0 ? nextIndex : rows.length - 1;
  return {
    rows,
    nextId: nextIndex >= 0 ? rows[nextIndex].id : "",
    anchorId: rows[anchorIndex]?.id || "",
  };
}

function etaLabel(event, now) {
  const minutes = Math.max(0, Math.round(event.startAt.diff(now, "minutes").minutes));
  if (minutes >= 180) return "";
  return `in ${minutes} min`;
}

function TimelineRow({ event, now, isNext, isDone, isFirst, isLast }) {
  const room = String(event.locationData?.room || "").trim();
  const doctor = String(event.doctorShort || event.doctor || "").trim();
  const type = String(event.type || "").trim();
  const sub = [doctor, type].filter(Boolean).join(" · ");
  const eta = isNext ? etaLabel(event, now) : "";
  return (
    <button
      type="button"
      data-event-id={event.id}
      onClick={() =>
        requestOpenCalendarAppointment({ appointmentId: event.id, startAt: event.startAt.toISO() })
      }
      title="Show on Calendar"
      className={cn(
        "relative flex w-full cursor-pointer items-center gap-3.5 py-[8.5px] text-left transition-colors duration-150",
        isDone && "opacity-50",
      )}
    >
      {/* Vertical connector through the dots; half-height at the ends. */}
      <span
        className={cn(
          "absolute left-[63px] w-0.5 -translate-x-1/2 bg-border",
          isFirst ? "top-1/2" : "top-0",
          isLast ? "bottom-1/2" : "bottom-0",
        )}
        aria-hidden
      />
      <span
        className={cn(
          "w-11 flex-none text-[13px] font-extrabold tabular-nums",
          isNext ? "text-primary" : "text-muted-foreground",
        )}
      >
        {event.startAt.toFormat("HH:mm")}
      </span>
      <span
        className={cn(
          "relative z-[1] h-2.5 w-2.5 flex-none rounded-full",
          isNext ? "bg-primary ring-4 ring-primary/20" : "bg-border",
        )}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-bold">{event.patient || event.title}</span>
        {sub && <span className="block truncate text-xs text-muted-foreground">{sub}</span>}
      </span>
      {eta && <span className="flex-none text-[11.5px] font-bold text-primary">{eta}</span>}
      {room && (
        <span
          className={cn(
            "flex-none rounded-md border px-[7px] py-[2.5px] text-[11px] font-bold",
            isNext ? "border-primary/45 text-primary" : "border-border text-muted-foreground",
          )}
        >
          {room}
        </span>
      )}
    </button>
  );
}

function ClinicSelector({ clinics, selectedClinicId, onSelect }) {
  const [open, setOpen] = useState(false);
  const selected = clinics.find((clinic) => clinic.id === selectedClinicId) || null;
  // Single-clinic users are auto-selected by the dashboard, so clearing would snap
  // straight back — offer the X only when there is a real choice.
  const showClear = Boolean(selected) && clinics.length > 1;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* The chip visual lives on a wrapper so the clear X can be a SIBLING button of the
          trigger — buttons must never nest. */}
      <div
        className={cn(
          "inline-flex max-w-[220px] items-center whitespace-nowrap rounded-lg border transition-colors duration-150",
          selected
            ? "border-border bg-card2 text-foreground hover:border-primary/45"
            : "border-primary/45 bg-primary/10 text-primary",
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "inline-flex min-w-0 cursor-pointer items-center gap-[7px] py-1.5 pl-[11px] text-[12.5px] font-bold",
              showClear ? "pr-1" : "pr-[11px]",
            )}
          >
            <span className="truncate">{selected ? selected.label : "Select clinic"}</span>
            <ChevronDown
              className={cn("h-3 w-3 flex-none", selected ? "text-muted-foreground" : "text-primary")}
            />
          </button>
        </PopoverTrigger>
        {showClear && (
          <button
            type="button"
            title="Clear clinic selection"
            onClick={() => onSelect("")}
            className="mr-[5px] grid h-[18px] w-[18px] flex-none cursor-pointer place-items-center rounded-[5px] text-muted-foreground transition-colors duration-150 hover:bg-accent/70 hover:text-foreground"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <PopoverContent align="end" className="w-56 p-1">
        {clinics.length === 0 && (
          <div className="px-3 py-2 text-xs text-muted-foreground">No clinics available.</div>
        )}
        {clinics.map((clinic) => (
          <button
            key={clinic.id}
            type="button"
            onClick={() => {
              onSelect(clinic.id);
              setOpen(false);
            }}
            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-[12.5px] font-bold hover:bg-accent/60"
          >
            <span className="truncate">{clinic.label}</span>
            {clinic.id === selectedClinicId && <Check className="h-3.5 w-3.5 flex-none text-primary" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The workbench "Today" card: the selected clinic's visit timeline around now. Clinic
 * choice persists per account (dashboardClinicId preference); with none chosen the card
 * shows its pick-a-clinic prompt and NEVER merges schedules across clinics. Data refetches
 * every minute; the "in N min" chip ticks on its own.
 */
export function DashboardTodayCard({ ownerUserId, clinics, selectedClinicId }) {
  const [events, setEvents] = useState(null); // null = loading for the current clinic
  const [now, setNow] = useState(() => DateTime.now());
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => setNow(DateTime.now()), NOW_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!selectedClinicId) {
      setEvents(null);
      return undefined;
    }
    setEvents(null);
    let cancelled = false;
    const seq = (requestSeqRef.current += 1);
    const load = async () => {
      try {
        const dayEvents = await getEventsForDay(DateTime.now());
        if (cancelled || seq !== requestSeqRef.current || !ownerMatches(ownerUserId)) return;
        setEvents(
          dayEvents.filter(
            (event) =>
              event.kind !== "slot" &&
              event.status !== "cancelled" &&
              event.tenantId === selectedClinicId,
          ),
        );
      } catch {
        if (cancelled || seq !== requestSeqRef.current) return;
        // Keep whatever is shown; the next tick retries. First load failure → empty day.
        setEvents((previous) => previous || []);
      }
    };
    void load();
    const timer = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedClinicId, ownerUserId]);

  const { rows, nextId, anchorId } = useMemo(
    () => buildDayTimeline(events || [], now),
    [events, now],
  );

  const handleSelect = (clinicId) => {
    setUserPreferences({ dashboardClinicId: clinicId });
  };

  // Open the timeline at the current moment. Keyed on the anchor id (a string), so the minute
  // refetch and the 30s clock tick — which both hand back fresh objects for the same visits —
  // do not re-scroll; only an actual change of "which visit is next" does.
  const timelineViewportRef = useRef(null);
  const userScrolledRef = useRef(false);
  const scrolledForRef = useRef("");

  // A manual scroll hands the reader control until they switch clinics: re-anchoring under
  // someone who scrolled back to the morning would look like the card fighting them.
  useEffect(() => {
    userScrolledRef.current = false;
    scrolledForRef.current = "";
  }, [selectedClinicId]);

  useEffect(() => {
    const viewport = timelineViewportRef.current;
    if (!viewport || !anchorId || userScrolledRef.current) return;
    if (scrolledForRef.current === anchorId) return;
    const node = viewport.querySelector(`[data-event-id="${CSS.escape(anchorId)}"]`);
    if (!node) return;
    scrolledForRef.current = anchorId;
    // scrollTop arithmetic rather than scrollIntoView: the latter also scrolls every ancestor,
    // which would move the whole dashboard. One row of lead-in keeps the previous visit visible
    // for context instead of parking the anchor flush against the top edge.
    const lead = node.offsetHeight;
    const max = Math.max(0, viewport.scrollHeight - viewport.clientHeight);
    viewport.scrollTop = Math.min(max, Math.max(0, node.offsetTop - lead));
  }, [anchorId, rows.length]);

  return (
    <Card className="w-full flex-none rounded-[14px] px-[22px] py-5 sm:max-[1199px]:min-w-0 sm:max-[1199px]:flex-1">
      <div className="mb-3.5 flex items-baseline justify-between gap-3">
        <div className="flex items-center gap-[9px] text-[15.5px] font-black tracking-[-0.1px]">
          <CalendarIcon className="h-4 w-4 text-primary" />
          Today
        </div>
        <ClinicSelector
          clinics={clinics}
          selectedClinicId={selectedClinicId}
          onSelect={handleSelect}
        />
      </div>

      {!selectedClinicId && (
        <div className="flex flex-col items-center justify-center gap-2.5 px-5 pb-[30px] pt-[34px] text-center">
          <CalendarIcon className="h-[26px] w-[26px] text-muted-foreground opacity-70" />
          <div className="text-sm font-extrabold">No clinic selected</div>
          <div className="max-w-[230px] text-[12.5px] text-muted-foreground">
            Pick a clinic above to see today&apos;s visits — schedules are never mixed across
            clinics.
          </div>
        </div>
      )}

      {selectedClinicId && events === null && (
        <div className="flex flex-col gap-3 py-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3.5">
              <Skeleton className="h-4 w-11" />
              <Skeleton className="h-2.5 w-2.5 rounded-full" />
              <Skeleton className="h-8 flex-1" />
            </div>
          ))}
        </div>
      )}

      {selectedClinicId && events !== null && rows.length === 0 && (
        <div className="flex flex-col items-center justify-center gap-2.5 px-5 pb-[30px] pt-[34px] text-center">
          <CalendarIcon className="h-[26px] w-[26px] text-muted-foreground opacity-70" />
          <div className="text-sm font-extrabold">No visits today</div>
          <div className="max-w-[230px] text-[12.5px] text-muted-foreground">
            This clinic has nothing scheduled today.
          </div>
        </div>
      )}

      {selectedClinicId && events !== null && rows.length > 0 && (
        // The bound sits on the VIEWPORT, not the Card: the ScrollArea Root is auto-height, so a
        // max-height above it would clip the day without ever making it scrollable. The card still
        // shrinks to fit a short day and only starts scrolling past the cap.
        <ScrollArea
          viewportRef={timelineViewportRef}
          viewportClassName="max-h-[19rem]"
          // Gestures, NOT the scroll event: the auto-scroll below sets scrollTop itself, and
          // listening for `scroll` would make it immediately mark the reader as having taken over.
          onWheel={() => {
            userScrolledRef.current = true;
          }}
          onTouchStart={() => {
            userScrolledRef.current = true;
          }}
        >
          <div className="flex flex-col pr-1">
            {rows.map((event, index) => (
              <TimelineRow
                key={event.id}
                event={event}
                now={now}
                isNext={event.id === nextId}
                isDone={event.startAt < now && event.id !== nextId}
                isFirst={index === 0}
                isLast={index === rows.length - 1}
              />
            ))}
          </div>
        </ScrollArea>
      )}
    </Card>
  );
}
